import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import { layout, LayoutProps } from "styled-system";

import Icon from "../Icon/Icon";
import Typography from "../Typography/Typography";
import LinkButton from "../Button/LinkButton";
import OverlayCard from "../OverlayCard/OverlayCard";
import { useKeyPress } from "./VehicleTypes";

const Layout = styled.div`
  display: flex;
  ${css({
    flexDirection: ["column", "row"],
    justifyContent: "space-between",
  })}
`;

const Recent = styled.div<LayoutProps>`
  ${layout}
  display: flex;
  flex-direction: column;
  ${css({
    alignItems: ["start", "flex-start"],
  })}
`;
const Saved = styled.div<LayoutProps>`
  text-align: right;
  display: flex;
  flex-direction: column;
  ${css({
    alignItems: ["start", "flex-end"],
  })}
  ${layout}
`;

const A = styled.a`
  line-height: 24px;
  outline: none;
  ${css({
    fontSize: "body",
    color: "secondary-darkest-grey",
    fontFamily: "normal",
    textDecoration: "none",
    "&:hover": {
      color: "primary-light-blue",
      textDecoration: "underline",
      cursor: "pointer",
    },
    "&:focus-within": {
      color: "primary-light-blue",
      textDecoration: "underline",
      cursor: "pointer",
    },
  })};
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  top: 27px;
  right: 27px;
  position: absolute;
  cursor: pointer;
  ${css({
    display: ["visible", "none"],
  })}
`;

type RecentSearchesProps = {
  searchResultsPageUrl: string;
  savedSearches: { label: string; url: string }[];
  recentSearches: { label: string; url: string }[];
  auctionViewRoot: string;
  close: () => void;
};

export default function RecentSearches({
  savedSearches = [],
  recentSearches = [],
  auctionViewRoot,
  close,
}: RecentSearchesProps) {
  const moreResultsRef = useRef<HTMLAnchorElement>(null);
  const savedSearchesRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const recentSearchesRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const downPress = useKeyPress("ArrowDown");
  const upPress = useKeyPress("ArrowUp");
  const rightPress = useKeyPress("ArrowRight");
  const leftPress = useKeyPress("ArrowLeft");
  const [cursor, setCursor] = useState(-1);
  const totalSearches = recentSearches.length + savedSearches.length;
  const left =
    savedSearches.length === 0 && recentSearches.length === 0
      ? [-71, 18]
      : [-65, 27, 23];

  useEffect(() => {
    const selectedItem =
      cursor >= recentSearches.length
        ? savedSearchesRefs.current[cursor - recentSearches.length]
        : recentSearchesRefs.current[cursor];

    if (cursor >= totalSearches && moreResultsRef && moreResultsRef.current) {
      moreResultsRef.current.focus();
    } else if (selectedItem) {
      selectedItem.focus();
    }
  }, [recentSearches, savedSearches, totalSearches, cursor]);

  useEffect(() => {
    if (totalSearches > 0 && downPress) {
      setCursor((prevState) =>
        prevState < totalSearches ? prevState + 1 : prevState
      );
    }
  }, [downPress, totalSearches]);

  useEffect(() => {
    if (totalSearches > 0 && upPress) {
      setCursor((prevState) => (prevState > 0 ? prevState - 1 : prevState));
    }
  }, [upPress, totalSearches]);

  useEffect(() => {
    if (savedSearches.length && rightPress) {
      setCursor(recentSearches.length);
    }
  }, [rightPress, recentSearches.length, savedSearches]);

  useEffect(() => {
    if (recentSearches.length && leftPress) {
      setCursor(0);
    }
  }, [leftPress, recentSearches]);

  return (
    <OverlayCard
      position="absolute"
      minWidth={["100vw", "100%"]}
      top={["52px", "60px"]}
      left={left}
      css="padding: 34px;  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);"
    >
      <CloseButton onClick={close}>
        <Icon name="times" weight="light" color="#3f403f" width="16px" />
      </CloseButton>
      <Layout>
        <Recent width={[1, 1 / 2]}>
          <Typography
            variant="label"
            color="grey-blue"
            css={`
              font-weight: 500;
              margin-bottom: 8px;
            `}
          >
            RECENT SEARCHES
          </Typography>
          {recentSearches.map((link, index) => (
            <A
              key={link.url}
              href={`${auctionViewRoot}${link.url}`}
              ref={(el) => (recentSearchesRefs.current[index] = el)}
            >
              {link.label}
            </A>
          ))}
        </Recent>
        <Saved width={[1, 1 / 2]}>
          <Typography
            variant="label"
            color="grey-blue"
            css={`
              font-weight: 500;
              margin-bottom: 8px;
            `}
          >
            SAVED SEARCHES
          </Typography>
          {savedSearches.map((link, index) => (
            <A
              key={link.url}
              href={`${auctionViewRoot}${link.url}`}
              ref={(el) => (savedSearchesRefs.current[index] = el)}
            >
              {link.label}
            </A>
          ))}
          <LinkButton
            text="View all saved searches"
            variant="tertiary"
            icon="arrow-right-solid"
            iconAlignment="right"
            paddingX={0}
            link={`${auctionViewRoot}/Buyer/StockAlerts?page=1`}
            shouldOpenInNewWindow={false}
            ref={moreResultsRef}
            css={`
              & div {
                margin-left: 3px;
              }
            `}
          />
        </Saved>
      </Layout>
    </OverlayCard>
  );
}
